import { useState, useEffect, useCallback } from "react";
import useApiCall from "./useApiCall";

/**
 * Custom hook to fetch data from an API endpoint.
 *
 * @param {string} endpoint - The API endpoint to fetch data from.
 * @param {Object} tokens - Authentication tokens required for the API call.
 * @param {Array} [dependencies=[]] - Dependencies array for the useEffect hook.
 * @param {Object} [initialState={}] - Initial state for the data.
 * @param {boolean} [autoFetch=true] - Flag to determine if data should be fetched automatically.
 * @returns {Object} - An object containing the fetched data, loading state, error state,
 *  fetchData function, and refresh function.
 * @property {any} data - The fetched data.
 * @property {boolean} loading - The loading state of the API call.
 * @property {Error|null} error - The error state of the API call.
 * @property {Function} fetchData - Function to manually fetch data from the API.
 * @property {Function} refresh - Function to refresh the data by re-fetching from the API.
 */

const useFetch = (
  endpoint,
  tokens,
  dependencies = [],
  initialState = {},
  autoFetch = true
) => {
  const [data, setData] = useState(initialState);
  const [error, setError] = useState(null);
  const { makeRequest, isLoading } = useApiCall();

  const fetchData = useCallback(async (url, fetchTokens = tokens) => {
    try {
      const response = await makeRequest(url, {
        method: "GET",
        authTokens: fetchTokens,
      });
      setData(response);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchData(endpoint);
    }
  }, [...dependencies]);

  return {
    data,
    loading: isLoading,
    error,
    fetchData,
    refresh: () => fetchData(endpoint),
  };
};

export default useFetch;
